import { useMemo, useState } from 'react'
import script from './stock-transfer-creator.js?raw'

// Stock Transfer: the work happens on portal.staging.m-kopa.net, riding the
// user's portal login. This page only hands out the script — as a bookmarklet
// (drag once, click on any portal page) or as text to paste into the console.
// The Launchpad host is Entra-gated, so the portal page cannot load the script
// as a <script src>; the bookmarklet therefore embeds the whole script.

const PORTAL_HOST = 'portal.staging.m-kopa.net'
const PORTAL_URL = `https://${PORTAL_HOST}/Inventory/InventoryTransfer/InventoryTransfers`
const TOGGLE_ID = 'mkopa-transfer-creator-toggle'

const body = script.replace(/^\/\/ ==UserScript==[\s\S]*?\/\/ ==\/UserScript==\s*/, '')
const version = script.match(/@version\s+(\S+)/)?.[1] ?? '?'
const bookmarklet =
  'javascript:' +
  encodeURIComponent(
    `if(location.host!=='${PORTAL_HOST}'){alert('Open this bookmark on ${PORTAL_HOST}');}` +
      `else if(document.getElementById('${TOGGLE_ID}')){document.getElementById('${TOGGLE_ID}').click();}` +
      `else{${body}}`,
  )

export function StockTransferView() {
  const [copied, setCopied] = useState(false)
  const href = useMemo(() => bookmarklet, [])

  const copy = async () => {
    await navigator.clipboard.writeText(body)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="module">
      <h2>Stock Transfer (Portal)</h2>
      <p className="muted">
        Creates a dispatched stock transfer on the staging portal with fresh IMEIs, so the Sales App
        stock-receiving flow has something to receive. It runs inside the portal page with your existing
        portal login — no token needed here.
      </p>

      <section>
        <h3>Install once</h3>
        <ol className="list list--numbered">
          <li>Show the bookmarks bar (<code>⌘⇧B</code>).</li>
          <li>
            Drag this onto it:&nbsp;
            <a
              className="bookmarklet"
              href={href}
              draggable
              onClick={(e) => {
                e.preventDefault()
                alert('Drag this button to your bookmarks bar instead of clicking it.')
              }}
            >
              📦 Stock transfer
            </a>
          </li>
        </ol>
        <p className="muted" style={{ marginTop: 12 }}>
          The bookmark holds a copy of the script (v{version}); re-drag it after an update.
        </p>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="ghost" onClick={copy}>{copied ? 'Copied ✓' : 'Copy script instead'}</button>
          <span className="muted">then paste into the DevTools console on any portal page</span>
        </div>
      </section>

      <section>
        <h3>Use</h3>
        <ol className="list list--numbered">
          <li>Open <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">the staging portal</a> and sign in.</li>
          <li>Click the <strong>📦 Stock transfer</strong> bookmark → a green 📦 button appears bottom-right.</li>
          <li>
            Pick device type(s) and a quantity for each — <strong>+ Add device type</strong> adds another row
            (the system only supports one device type per transfer, so each row becomes its own transfer) — plus
            shop and supplier (defaults: M-KOPA X30 → SC030 Matunda).
          </li>
          <li>
            <strong>Transfers (bulk)</strong> repeats the whole set: leave it at 1 for a single transfer, or
            set 100 / 1000 to bulk-create that many for the shop. It confirms above 10, shows live progress
            per transfer, and can be cancelled mid-run.
          </li>
          <li>
            Press <strong>Create test transfer</strong>. A new tab opens with a Code 128 barcode for every IMEI,
            grouped by waybill, ready to scan from the screen or print (<code>⌘P</code>). The panel also prints the
            IMEIs as text, and <strong>🏷 Barcodes</strong> reopens the last run's page if the tab was blocked or closed.
          </li>
        </ol>
      </section>

      <section>
        <h3>What it does</h3>
        <table className="steps">
          <tbody>
            <tr><td>1</td><td /><td><code>POST /Inventory/MeteredItem/UploadManifest</code> — one consignment per device type holding every IMEI for the run, generated CSV, into the Production Line</td></tr>
            <tr><td>2</td><td /><td><code>POST /Inventory/Consignment/CloseConsignment</code> — per consignment</td></tr>
            <tr><td>3</td><td /><td><code>POST /Inventory/InventoryTransfer/AddTransfer</code> — Production Line → shop, then reads the new TransferId (repeated per transfer in bulk runs)</td></tr>
            <tr><td>4</td><td /><td><code>POST /Inventory/InventoryTransfer/AddTransferItem</code> — per IMEI</td></tr>
            <tr><td>5</td><td /><td><code>POST /Inventory/InventoryTransfer/FinishLoadingTransfer</code> — transfer is In Transit</td></tr>
            <tr><td>6</td><td /><td>Opens a tab with the IMEI barcodes of every transfer created — generated in the script, nothing is sent anywhere</td></tr>
          </tbody>
        </table>
        <p className="muted" style={{ marginTop: 12 }}>
          IMEIs are <code>{'<prefix><clock digits><counter>'}</code> so runs never collide (the counter widens
          for bulk runs); paste your own list to override — it must match the run's total device count.
          Every step checks the portal's response and stops with its message on failure; a cancelled or failed
          bulk run keeps the transfers already dispatched and reports how far it got.
        </p>
      </section>
    </div>
  )
}
